import { queriesSharedFileRequestConfigurations } from "@query";
import { WSInfoBox } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  RequirementDefinition,
  RequirementType,
  SearchRowEngagementStatusRollup
} from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { WSQueries } from "../../../../query/WSQuery";
import { useUserId } from "../../../../query/hooks/helpers";
import { useXWingspanExpansion } from "../../../../shared/hooks/useXWingspanExpansion";
import { queriesBackgroundCheckConfigurations } from "../../../../v3/query/queriesBackgroundCheckConfigurations";
import { queriesBackgroundCheckPackages } from "../../../../v3/query/queriesBackgroundCheckPackages";
import { queriesInsuranceMonitorConfigurations } from "../../../../v3/query/queriesInsuranceMonitorConfigurations";
import { queriesPayeeRows } from "../../../../v3/query/queriesPayeeRows";
import { getRequirementDefinitionTemplateByParams } from "../../../RequirementDefinitions/utils/getRequirementDefinitionTemplateByParams";
import { REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS } from "../../modules/EmployeeTaxForms/constants";
import { selectorPolicyTypesFromSettings } from "../../modules/InsuranceCoverage/selectors/selectorPolicyTypesFromSettings";

export type AddRequirementInformationProps = {
  requirementDefinition?: RequirementDefinition;
  engagementId: string;
};

const USER_IDS_WITH_HIDDEN_PRICE = [
  //https://linear.app/wingspan/issue/PLA-855/fe-hide-idv-requirement-pricing-for-wsol-dbhpuhdhh7dute93vcqxvf
  "DbhpUHdhH7Dute93VCqXvF"
];

export const AddRequirementInformation: React.FC<
  AddRequirementInformationProps
> = ({ engagementId, requirementDefinition }) => {
  const userId = useUserId();

  const xWingspanAccount = useXWingspanExpansion();
  const queryPayeeRowsSummary = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: {
        engagementStatus: {
          noneOf: [SearchRowEngagementStatusRollup.INACTIVE]
        },
        engagements_engagementId: { eq: engagementId }
      },
      page: { size: 1 },
      xWingspanAccount
    },
    {
      select: list => list.pagination?.totalSize ?? 0,
      enabled:
        !!requirementDefinition?.type &&
        [
          RequirementType.BACKGROUND_CHECK,
          RequirementType.DOCUMENT_UPLOAD,
          RequirementType.BIOMETRIC_IDENTITY_VERIFICATION
        ].includes(requirementDefinition?.type)
    }
  );

  const queryBackgroundChecksSettings =
    queriesBackgroundCheckConfigurations.getBackgroundCheckConfiguration.useQuery(
      { backgroundCheckConfigurationId: requirementDefinition?.dataSourceId! },
      {
        enabled:
          !!requirementDefinition?.dataSourceId &&
          requirementDefinition?.type === RequirementType.BACKGROUND_CHECK
      }
    );

  // v1 embedded the whole package on the settings response; v3 carries only
  // `packageId`, so the per-check cost needs the package fetched separately.
  const queryBackgroundCheckPackage =
    queriesBackgroundCheckPackages.getBackgroundCheckPackage.useQuery(
      {
        backgroundCheckPackageId: queryBackgroundChecksSettings.data
          ?.packageId as string
      },
      { enabled: !!queryBackgroundChecksSettings.data?.packageId }
    );

  const queryMonitorSettings =
    queriesInsuranceMonitorConfigurations.getInsuranceMonitorConfiguration.useQuery(
      {
        insuranceMonitorConfigurationId: requirementDefinition?.dataSourceId!
      },
      {
        enabled:
          !!requirementDefinition?.dataSourceId &&
          requirementDefinition?.type === RequirementType.INSURANCE_COVERAGE
      }
    );

  const querySharedFilesSettings =
    queriesSharedFileRequestConfigurations.getSharedFileRequestConfiguration.useQuery(
      {
        sharedFileRequestConfigurationId: requirementDefinition?.dataSourceId!
      },
      {
        enabled:
          !!requirementDefinition?.dataSourceId &&
          requirementDefinition?.type === RequirementType.DOCUMENT_UPLOAD
      }
    );

  const defaultView: React.ReactNode = (
    <WSInfoBox
      p="L"
      title="Requirement added will impact your existing contractors"
    >
      Your existing contractors will be notified of the requirement added to
      this engagement. The requirement will also apply to any future contractors
      added to this engagement.
    </WSInfoBox>
  );

  const views: Partial<
    Record<
      RequirementType | typeof REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS,
      React.ReactNode
    >
  > = {
    [RequirementType.BACKGROUND_CHECK]: (
      // the package read is chained behind the configuration and stays disabled
      // until it resolves, so gate on the configuration — a disabled query is
      // never success nor error, and listing it here would hang the loader
      <WSQueries
        queries={{ queryPayeeRowsSummary, queryBackgroundChecksSettings }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData }) => {
          return (
            <>
              <WSInfoBox
                p="L"
                title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
              >
                {queryPayeeRowsSummaryData} existing contractors will be
                notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              <WSInfoBox p="L" title="Background check package">
                The selected background check package is the Wingspan Standard
                package and will cost{" "}
                {formatMoney(
                  queryBackgroundCheckPackage.data?.costPerCheck?.amount || 0,
                  "USD"
                )}{" "}
                per check.
              </WSInfoBox>
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.DOCUMENT_UPLOAD]: (
      <WSQueries
        queries={{ queryPayeeRowsSummary, querySharedFilesSettings }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData, querySharedFilesSettingsData }) => (
          <>
            <WSInfoBox
              p="L"
              title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
            >
              {queryPayeeRowsSummaryData} existing contractors will be notified
              of the requirement added to this engagement. The requirement will
              also apply to any future contractors added to this engagement.
            </WSInfoBox>

            {querySharedFilesSettingsData.cost ? (
              <WSInfoBox p="L" title="Document verification requirement">
                The selected document verification requirement will cost{" "}
                {formatMoney(querySharedFilesSettingsData.cost, "USD")} per
                verification.
              </WSInfoBox>
            ) : null}
          </>
        )}
      </WSQueries>
    ),
    [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: (
      <WSQueries
        queries={{ queryPayeeRowsSummary }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData }) => {
          const isPriceHidden = USER_IDS_WITH_HIDDEN_PRICE.includes(userId);

          return (
            <>
              <WSInfoBox
                p="L"
                title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
              >
                {queryPayeeRowsSummaryData} existing contractors will be
                notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>
              {isPriceHidden ? null : (
                <WSInfoBox p="L" title="Document verification requirement">
                  The selected document verification requirement will cost{" "}
                  {formatMoney(5, "USD")} per verification.
                </WSInfoBox>
              )}
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.SIGNATURE]: defaultView,
    [RequirementType.INSURANCE_COVERAGE]: (
      <WSQueries
        queries={{ queryPayeeRowsSummary, queryMonitorSettings }}
        renderErrors={() => defaultView}
      >
        {({ queryPayeeRowsSummaryData, queryMonitorSettingsData }) => {
          const template = getRequirementDefinitionTemplateByParams({
            type: RequirementType.INSURANCE_COVERAGE,
            policyType: selectorPolicyTypesFromSettings(
              queryMonitorSettingsData
            )
          });

          return (
            <>
              <WSInfoBox
                p="L"
                title={`Requirement added will impact ${queryPayeeRowsSummaryData} existing contractors`}
              >
                {queryPayeeRowsSummaryData} existing contractors will be
                notified of the requirement added to this engagement. The
                requirement will also apply to any future contractors added to
                this engagement.
              </WSInfoBox>

              {template.costPerSubmission ? (
                <WSInfoBox p="L" title="Document verification requirement">
                  The selected document verification requirement will cost{" "}
                  {formatMoney(template.costPerSubmission, "USD")} per
                  verification.
                </WSInfoBox>
              ) : null}
            </>
          );
        }}
      </WSQueries>
    ),
    [RequirementType.REGISTRATION]: defaultView,
    [RequirementType.TAX_VERIFICATION]: defaultView,
    [RequirementType.PAYOUT_METHOD]: defaultView,
    [REQUIREMENT_TYPE_EMPLOYEE_TAX_FORMS]: defaultView
  };

  return (
    <>
      {requirementDefinition?.type
        ? (views[requirementDefinition.type] ?? defaultView)
        : defaultView}
    </>
  );
};
